import type { LangPickerProps } from '@ui/i18n/LangPicker';

import { parseLanguageVersion } from '@readme/i18n';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useProjectStore, useSuperHubStore } from '@core/store';
import LangPicker from '@ui/i18n/LangPicker';

export default function LanguagePicker({ icon, label }: Pick<LangPickerProps, 'icon' | 'label'>) {
  const [subdomain, enabledLanguages] = useProjectStore(s => [s.data.subdomain, s.data.i18n.languages]);
  const [currentLanguage, currentVersion, versions, isGroup] = useSuperHubStore(s => [
    s.language,
    s.getCurrentVersion(),
    s.versions.data.data,
    s.enterprise?.isGroup,
  ]);
  const { pathname, search, hash } = useLocation();

  // Determine if we are on the enterprise group's Global Language Page (GLP)
  // A GLP is not versioned, so we need to handle language URLs differently
  const isGLP = useMemo(() => {
    return pathname === '/' || isGroup;
  }, [pathname, isGroup]);

  const buildLanguageUrl = useCallback(
    (lang: string) => {
      if (isGLP) {
        return `/${lang === 'en' ? '' : lang}`;
      }

      const cleanVersion = parseLanguageVersion(currentVersion.name).version; // remove existing language suffix if present
      const langSuffix = lang === 'en' ? '' : `-${lang}`; // only add suffix if not default language
      return `/${subdomain}/v${cleanVersion}${langSuffix}${pathname}${search}${hash}`;
    },
    [currentVersion, pathname, search, hash, subdomain, isGLP],
  );

  const languages = useMemo(() => {
    if (isGLP) {
      return enabledLanguages;
    }
    const baseVersion = parseLanguageVersion(currentVersion.name).version;
    const validLanguages = new Set<string>();

    versions.forEach(version => {
      const parsedVersion = parseLanguageVersion(version.name);

      if (parsedVersion.version !== baseVersion) return;
      validLanguages.add(parsedVersion.lang || 'en');
    });

    return enabledLanguages.filter(lang => validLanguages.has(lang));
  }, [currentVersion.name, enabledLanguages, isGLP, versions]);

  return (
    <LangPicker
      buildHref={buildLanguageUrl}
      buttonClassName="rm-Header-top-link"
      circular={false}
      enabledLanguages={languages}
      ghost
      icon={icon}
      initialLanguage={currentLanguage}
      kind="secondary"
      label={label}
    />
  );
}
